import httpRequestLib, { createQueryParams } from './Request';
import { pushNotificationURL, qsApiHost } from '../../config';
import {
  getCompanyId,
  getCurrencyCode,
  getHashedBroadcastReceiver,
  getSimulatedVisitor,
  getVisitorId as amalgamGetVisitorId,
  isSimulation
} from './Amalgam';
import { getLandingPage } from './Experiments';
import { putCookie } from './LocalStorage';
import { getShowcaseId, getShowcaseSlug } from './Showcase';
import { getCatalogueId } from './Catalogue';
import { catalogueOpened } from './Socket';
import { getUserId } from './User';

const APP_VERSION = process.env.APP_VERSION;

const requestQueue = [];
let _visitor = null;
let _openId = null;
let createOrGetVisitorPromise = null;
let viewTimerInitiated = false;
let view = null;
let openProductFired = {};
let updateAfterVisitorInitialized = false;

const request = (req, options = {}) =>
  new Promise((resolve, reject) => {
    if (!_visitor || !_visitor.id) {
      requestQueue.push({
        req,
        resolve,
        reject,
        options
      });

      return;
    }

    const { attachVisitor = false } = options;

    if (req.method === 'POST') {
      req.params = req.params || {};
      req.params.visitorId = _visitor.id;

      if (attachVisitor) {
        req.params.visitor = _visitor;
      }
    }

    if (req.method === 'DELETE') {
      req.params = req.params || {};
      req.params.visitorId = _visitor.id;

      if (attachVisitor) {
        req.params.visitor = _visitor;
      }
    }

    req.headers = req.headers || {};
    req.headers['web-client-version'] = APP_VERSION;

    httpRequestLib(req).then(resolve).catch(reject);
  });

const handledRequest = (req, callback) => {
  request(req)
    .then((response) => {
      if (callback) {
        callback(null, response);
      }
    })
    .catch((err) => {
      if (callback) {
        callback(err);
      }
    });
};

export const getVariantsDefaultImages = (parentProductId) =>
  httpRequestLib({
    method: 'GET',
    url: `/api/product/variants/default-picture?productId=${parentProductId}`
  });

export const getCatalogueInventory = (catalogueId) =>
  httpRequestLib({
    method: 'GET',
    url: `${qsApiHost}/inventory/catalogue?catalogueId=${catalogueId}`
  });

export const getSkeletonVisitor = (visitorId) =>
  httpRequestLib({
    method: 'GET',
    url: `/api/visitor/skeleton?visitorId=${visitorId}`
  });

export const updateSkeletonVisitor = (updates) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/visitor/skeleton',
    params: {
      companyId: getCompanyId(),
      visitorId: _visitor.id,
      updates
    }
  }).then(() => {
    _visitor = { ..._visitor, ...updates };
    return Promise.resolve(_visitor);
  });

export const updateAfterVisitorInitialization = () => {
  if (updateAfterVisitorInitialized) {
    return Promise.resolve();
  }

  updateAfterVisitorInitialized = true;

  const landingPage = getLandingPage();

  const params = {};

  if (isSimulation()) {
    const simulatedVisitor = getSimulatedVisitor();
    params.name = simulatedVisitor.name;
    params.phone = simulatedVisitor.phone;
  }

  if (
    (landingPage === 'company-close' || landingPage === 'company-close-otp') &&
    window.e164Number
  ) {
    params.phone = window.e164Number;
  }

  if (landingPage === 'company-close-otp' && window.e164PhoneVerified) {
    params.phoneVerified = window.e164PhoneVerified;
  }

  if (!Object.keys(params).length) {
    return Promise.resolve();
  }

  return updateSkeletonVisitor(params);
};

export const getVisitorId = () => (_visitor ? _visitor.id : amalgamGetVisitorId());

const getVisitorCountryCode = () =>
  request({
    method: 'GET',
    url: `${qsApiHost}/v1/config/ip-geo`
  });

export const createOrGetVisitor = ({ savedVisitorId }) => {
  if (createOrGetVisitorPromise) {
    return createOrGetVisitorPromise;
  }

  if (_visitor) {
    return new Promise((resolve) => resolve(_visitor));
  }

  createOrGetVisitorPromise = new Promise((resolve, reject) => {
    const params = {
      id: savedVisitorId || null,
      phone: null,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight
    };

    const options = {
      method: 'POST',
      url: `${qsApiHost}/v3/visitor`,
      params,
      headers: {
        'web-client-version': APP_VERSION
      }
    };

    httpRequestLib(options)
      .then((visitor) => {
        _visitor = visitor;

        _visitor.getPhone = () => _visitor.phone;

        _visitor.setIdentity = (identity) => {
          Object.keys(identity).forEach((key) => {
            if (identity[key] === undefined || typeof identity[key] === 'function') {
              delete identity[key];
            }
          });

          return new Promise((resolve) => {
            updateSkeletonVisitor(identity)
              .then(() => resolve(true))
              .catch(() => resolve(false));
          });
        };

        _visitor.getCountryCode = () => {
          const { location } = _visitor;
          if (location) {
            return Promise.resolve(location.countryCode);
          }
          return getVisitorCountryCode().then((data) => {
            visitor.location = { ...data };
            return data.countryCode;
          });
        };

        resolve(_visitor);
        createOrGetVisitorPromise = null;

        if (!savedVisitorId) {
          putCookie('_visitor_id', _visitor.id);
        }

        requestQueue.forEach((data) => {
          request(data.req, data.options).then(data.resolve).catch(data.reject);
        });
      })
      .catch((err) => {
        reject(err);
        createOrGetVisitorPromise = null;
      });
  });

  return createOrGetVisitorPromise;
};

export const getCompanyCheckoutFields = (fetchVisible) =>
  request({
    method: 'GET',
    url: `/api/order/company-checkout-fields?companyId=${getCompanyId()}&all=${!fetchVisible}`
  });

export const updateInquiryItemCount = (params) =>
  request({
    method: 'PUT',
    url: '/api/order/inquiry/itemCount/v2',
    params
  });

export const setInquiryItemCount = (params) =>
  request({
    method: 'PUT',
    url: '/api/order/inquiry/itemCount',
    params
  });

export const updateInquiryText = (inquiryId, inquiryText) =>
  request({
    method: 'PUT',
    url: '/api/order/inquiry/inquiryText',
    params: { inquiryId, inquiryText }
  });

export const deleteInquiry = (inquiryId) =>
  request({
    method: 'DELETE',
    url: '/api/order/inquiry',
    params: {
      inquiryId
    }
  });

export const newOrder = (
  productId,
  catalogueId,
  inquiryText,
  pictureId,
  itemCount,
  sentFromBulkOrderingView
) =>
  request({
    method: 'POST',
    url: '/api/order/v2',
    params: {
      productId,
      catalogueId,
      inquiryText,
      pictureId,
      itemCount,
      openId: _openId,
      sentFromBulkOrderingView
    }
  });

export const updateDimension = ({ productId, pictureId, width, height }) =>
  request({
    method: 'PUT',
    url: '/products/picture-meta',
    params: {
      productId,
      pictureId,
      width,
      height
    }
  });

export const sendInquiryPush = (orderId, inquiryId) => {
  httpRequestLib({
    method: 'POST',
    url: pushNotificationURL,
    params: {
      type: 'inquiry',
      orderId,
      inquiryId
    }
  }).catch(() => {});
};

export const addInquiry = (
  orderId,
  productId,
  catalogueId,
  inquiryText,
  pictureId,
  itemCount,
  sentFromBulkOrderingView
) =>
  request({
    method: 'POST',
    url: '/api/order/inquiry',
    params: {
      orderId,
      productId,
      catalogueId,
      inquiryText,
      pictureId,
      itemCount,
      openId: _openId,
      sentFromBulkOrderingView
    }
  });

export const finalizeOrder = ({
  orderId,
  visitorBillingDetails,
  customerBillingDetails,
  checkoutFields,
  shippingAddress,
  couponCode,
  payUsing,
  payUsingWithPartialCOD,
  shippingModeId,
  orderFrom
}) => {
  if (
    customerBillingDetails &&
    visitorBillingDetails &&
    !visitorBillingDetails.email &&
    customerBillingDetails.email
  ) {
    visitorBillingDetails.email = customerBillingDetails.email;
  }
  return request({
    method: 'POST',
    url: '/api/order/finalize',
    params: {
      orderId,
      visitorBillingDetails,
      customerBillingDetails,
      checkoutFields,
      shippingAddress,
      couponCode,
      payUsing,
      shippingModeId,
      orderFrom,
      payUsingWithPartialCOD
    }
  });
};

export const finalizeOrderWithRazorpayPayment = (razorpayResponse) =>
  request({
    method: 'POST',
    url: '/api/order/finalize/razorpay',
    params: razorpayResponse
  });

export const receiveOrderByEmail = (orderId, { emails } = {}) =>
  request({
    method: 'POST',
    url: `${qsApiHost}/order/email/catalogue`,
    params: {
      orderId,
      emails,
      visitorId: _visitor.id
    }
  });

export const receiveOrderByWhatsapp = (orderId) =>
  request({
    method: 'POST',
    url: '/api/order/whatsapp',
    params: {
      orderId
    }
  });

export const logoutUser = () =>
  request({
    method: 'POST',
    url: '/api/reseller/logout',
    params: { visitorId: getVisitorId() }
  });

const renderBroadcastCatalogue = (hashedBroadcastReceiver, openId) => {
  request({
    method: 'POST',
    url: `${qsApiHost}/v1/broadcast/render-catalogue`,
    params: {
      hashedBroadcastReceiver,
      showcaseId: getShowcaseId(),
      catalogueId: getCatalogueId(),
      openId
    }
  }).catch(() => {
    setTimeout(() => {
      renderBroadcastCatalogue(hashedBroadcastReceiver);
    }, 2000);
  });
};

export const openCatalogue = ({ showcaseSlug, openId } = {}) => {
  _openId = openId;

  handledRequest({
    method: 'POST',
    url: `${qsApiHost}/v2/showcase-visitor-meta/open-catalogue`,
    params: {
      slug: showcaseSlug || getShowcaseSlug(),
      openId
    }
  });

  catalogueOpened(
    getCompanyId(),
    getCatalogueId(),
    getVisitorId(),
    openId,
    showcaseSlug || getShowcaseSlug()
  );

  const hashedBroadcastReceiver = getHashedBroadcastReceiver();

  if (hashedBroadcastReceiver) {
    renderBroadcastCatalogue(hashedBroadcastReceiver, openId);
  }
};

const sendViewRequest = (callback) => {
  if (!view) {
    return;
  }

  const localView = JSON.parse(JSON.stringify(view || {}));
  view = null;

  const viewedProducts = [];

  Object.keys(localView.products || {}).forEach((productId) => {
    const product = localView.products[productId] || {};

    Object.keys(product).forEach((imageId) => {
      const image = product[imageId] || {};

      if (!image.eventId) {
        return;
      }

      viewedProducts.push({
        productId,
        imageId,
        eventId: image.eventId,
        viewTime: image.viewTime || 0
      });
    });
  });

  const body = {
    method: 'POST',
    url: `${qsApiHost}/v2/showcase-visitor-meta/view`,
    params: {
      openId: _openId,
      slug: localView.showcaseSlug || getShowcaseSlug(),
      view: {
        catalogue: localView.catalogue || null,
        products: viewedProducts
      }
    }
  };

  handledRequest(body, callback);
};

const startRegularTimer = (callback) => {
  setInterval(() => {
    sendViewRequest(callback);
  }, 5000);
};

const startInitialTimer = (callback) => {
  setTimeout(() => {
    sendViewRequest(callback);
    startRegularTimer(callback);
  }, 1000);
};

export const startViewTimer = (openId, callback) => {
  if (viewTimerInitiated) {
    return;
  }

  viewTimerInitiated = true;

  _openId = openId;

  startInitialTimer(callback);
};

export const openProduct = (body) => {
  const openCacheKey = body.productId + '_' + body.imageId + '_' + body.eventId;
  if (openProductFired[openCacheKey]) {
    return;
  }

  openProductFired[openCacheKey] = true;

  handledRequest({
    method: 'POST',
    url: qsApiHost + '/v2/showcase-visitor-meta/open-product',
    params: {
      slug: getShowcaseSlug(),
      openId: _openId,
      productId: body.productId,
      imageId: body.imageId,
      eventId: body.eventId
    }
  });
};

export const openTag = (body) => {
  handledRequest({
    method: 'POST',
    url: qsApiHost + '/v1/showcase-visitor-meta/open-tag',
    params: {
      slug: getShowcaseSlug(),
      tag: body.tag,
      eventId: body.eventId
    }
  });
};

export const viewProduct = (viewTime, productId, imageId, eventId, { showcaseSlug }) => {
  if (!view) {
    view = {};
  }

  view.showcaseSlug = showcaseSlug;

  if (!view.products) {
    view.products = {};
  }

  if (!view.products[productId]) {
    view.products[productId] = {};
  }

  view.products[productId][imageId] = {
    eventId,
    viewTime
  };
};

export const viewCatalogue = (viewTime, { showcaseSlug }) => {
  if (!view) {
    view = {};
  }

  view.showcaseSlug = showcaseSlug;
  view.catalogue = viewTime;
};

export const cancelAnalytics = () => {
  _openId = null;
  viewTimerInitiated = false;
  view = null;
  openProductFired = {};
};

export const downloadPDF = ({ catalogueId, catalogueLink, companyId }) =>
  httpRequestLib({
    method: 'GET',
    url: `${qsApiHost}/catalogue/pdf/download-web?catalogueId=${catalogueId}&catalogueLink=${catalogueLink}&companyId=${companyId}`
  });

export const getOrder = ({ visitorId, companyId }) =>
  httpRequestLib({
    method: 'GET',
    url: `/api/order?visitorId=${visitorId}&companyId=${companyId}&t=${Date.now()}`
  });

export const getOrderByOrderId = ({ orderId }) =>
  httpRequestLib({
    method: 'GET',
    url: `api/orders/${orderId}${createQueryParams([
      {
        key: 'companyId',
        value: getCompanyId()
      }
    ])}`
  });

export const getInquiry = (inquiryId) =>
  httpRequestLib({
    method: 'GET',
    url: `/api/order/inquiry?inquiryId=${inquiryId}&r=${Date.now()}`
  });

export const getBulkInquiry = (orderId) =>
  httpRequestLib({
    method: 'GET',
    url: `/api/order/bulk-inquiry?orderId=${orderId}&r=${Date.now()}`
  });

export const geocodePincode = (pincode, country) =>
  httpRequestLib({
    method: 'GET',
    url: `/api/config/geocode/pincode?pincode=${pincode}&country=${country}`
  });

export const getLatestShippingAddress = (visitorId) =>
  httpRequestLib({
    method: 'GET',
    url: `/api/visitor/shipping-address/latest?visitorId=${visitorId}`
  });

export const timedUpdateShippingAddress = (visitorId, shippingAddressId, updates) =>
  httpRequestLib({
    method: 'POST',
    url: `${qsApiHost}/v2/visitor/shipping-address/timed`,
    params: { visitorId, shippingAddressId, updates }
  });

export const validateShippingAddress = (
  orderId,
  visitorBillingDetails,
  customerBillingDetails,
  shippingAddress,
  isResellerOrder
) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/visitor/shipping-address/validate/v2',
    params: {
      orderId,
      catalogueId: getCatalogueId(),
      companyId: getCompanyId(),
      visitorBillingDetails,
      customerBillingDetails,
      shippingAddress,
      isResellerOrder
    }
  });

export const applyCoupon = ({ code, orderId }) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/apply-coupon',
    params: { code, orderId }
  });

export const removeCoupon = ({ orderId }) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/remove-coupon',
    params: { orderId }
  });

export const createRazorpayOrder = ({
  orderId,
  shippingAddressId,
  billingDetails,
  shippingAddress
}) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/razorpay-order',
    params: { orderId, shippingAddressId, billingDetails, shippingAddress }
  });

export const savePaymentMode = ({ orderId, paymentMode }) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/order/payment-mode',
    params: { orderId, paymentMode }
  });

export const savePaymentType = ({ orderId, paymentType }) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/order/payment-type',
    params: { orderId, paymentType }
  });

export const getSingleVariantMeta = ({ companyId, variantId, acceptLanguage }) =>
  httpRequestLib({
    method: 'GET',
    url: `/api/variant-meta?companyId=${companyId}&variantId=${variantId}&acceptLanguage=${acceptLanguage}`
  });

export const getCountriesListForCompany = (companyId) =>
  httpRequestLib({
    method: 'GET',
    url: `/api/v1/shipping/company-countries?companyId=${companyId}`
  });

export const getAllStatesForCountry = (countryId) =>
  httpRequestLib({
    method: 'GET',
    url: `/api/v1/shipping-cost/all-states?countryId=${countryId}`
  });

export const getAllCitiesForState = (countryId, stateId) =>
  httpRequestLib({
    method: 'GET',
    url: `/api/v1/shipping-cost/all-cities?stateId=${stateId}&countryId=${countryId}`
  });

export const getShipmentModes = (
  { countryId, stateId, cityId, pincode, paymentOption, weight, orderPrice },
  requestUpdated
) =>
  httpRequestLib({
    method: 'GET',
    url: `/api/order/shipping-details${createQueryParams([
      {
        key: 'companyId',
        value: getCompanyId()
      },
      {
        key: 'countryId',
        value: countryId
      },
      {
        key: 'stateId',
        value: stateId
      },
      {
        key: 'cityId',
        value: cityId
      },
      {
        key: 'pincode',
        value: pincode
      },
      {
        key: 'paymentOption',
        value: paymentOption
      },
      {
        key: 'weight',
        value: weight
      },
      {
        key: 'orderPrice',
        value: orderPrice
      }
    ])}`,
    requestUpdated
  });

export const getPaginatedAmalgam = (productIds) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/amalgam/paginated',
    params: {
      companyId: getCompanyId(),
      catalogueId: getCatalogueId(),
      productIds,
      currencyCode: getCurrencyCode()
    }
  });

export const getCatalogueFilters = () => {
  const queryString = createQueryParams([{ key: 'catalogueId', value: getCatalogueId() }]);
  return httpRequestLib({ method: 'GET', url: `/api/search/filters${queryString}` });
};

export const getRenewalReminderCardStatus = () => {
  const queryString = createQueryParams([{ key: 'companyId', value: getCompanyId() }]);
  return httpRequestLib({ method: 'GET', url: `/api/company/renewal-reminder${queryString}` });
};

/**
 * get filtered paginated amalgam
 * @param {string} searchTerm
 * @param {string} selectedTag
 * @param {Object} filters
 * @returns {Promise} APi request Promise
 */
export const getFilteredPaginatedAmalgam = ({
  onSaleTagSelected,
  inStockTagSelected,
  searchTerm,
  filters,
  page,
  returnAggregatedTags,
  catalogueShowOutOfStock,
  filteredRequest
}) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/amalgam/paginated/filtered',
    params: {
      companyId: getCompanyId(),
      catalogueId: getCatalogueId(),
      currencyCode: getCurrencyCode(),
      onSaleTagSelected,
      inStockTagSelected,
      searchTerm,
      filters,
      page,
      returnAggregatedTags,
      catalogueShowOutOfStock
    },
    requestUpdated: filteredRequest
  });

export const getTopProductsBasedOnTags = ({
  tagIdsList,
  onSaleTagSelected,
  catalogueId,
  catalogueShowOutOfStock,
  excludeProductData
}) => {
  if (!catalogueId) {
    return Promise.resolve({});
  }
  return httpRequestLib({
    method: 'POST',
    url: '/api/amalgam/paginated/tag-based-top-products',
    params: {
      catalogueId,
      companyId: getCompanyId(),
      currencyCode: getCurrencyCode(),
      tagIdsList,
      onSaleTagSelected,
      catalogueShowOutOfStock,
      excludeProductData
    }
  });
};

export const getFilteredTaggedPaginatedAmalgam = ({
  selectedTag,
  onSaleTagSelected,
  from,
  catalogueId,
  pageSize,
  catalogueShowOutOfStock,
  excludeProductData,
  lastProductPosition
}) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/amalgam/paginated/tag-filtered',
    params: {
      catalogueId,
      companyId: getCompanyId(),
      currencyCode: getCurrencyCode(),
      selectedTag,
      onSaleTagSelected,
      from,
      pageSize,
      catalogueShowOutOfStock,
      excludeProductData,
      lastProductPosition
    }
  });

export const firebaseAnalyticsOpenEvent = ({
  openId,
  domainTag,
  showcaseId,
  catalogueId,
  visitorId,
  referrerType,
  referrerValue
}) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/firebase-analytics/open-event',
    params: {
      openId,
      domainTag,
      showcaseId,
      catalogueId,
      visitorId,
      referrerType,
      referrerValue
    }
  });

export const firebaseAnalyticsOpenEventViewTime = ({
  openId,
  showcaseId,
  visitorId,
  viewingSince,
  domainTag,
  catalogueId,
  referrerType,
  referrerValue
}) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/firebase-analytics/open-event-view-time',
    params: {
      openId,
      showcaseId,
      visitorId,
      viewingSince,
      domainTag,
      catalogueId,
      referrerType,
      referrerValue
    }
  });

export const firebaseAnalyticsViewEvent = ({
  eventId,
  showcaseId,
  catalogueId,
  visitorId,
  openId,
  viewingSince,
  productId,
  imageId
}) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/firebase-analytics/view-event',
    params: {
      eventId,
      showcaseId,
      catalogueId,
      visitorId,
      openId,
      viewingSince,
      productId,
      imageId
    }
  });

export const firebaseAnalyticsViewEventMore = ({
  eventId,
  showcaseId,
  visitorId,
  viewingSince,
  productId,
  imageId,
  catalogueId,
  openId
}) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/firebase-analytics/view-event-more',
    params: {
      eventId,
      showcaseId,
      visitorId,
      viewingSince,
      productId,
      imageId,
      catalogueId,
      openId
    }
  });

export const firebaseAnalyticsProductSelectedEvent = ({
  eventId,
  showcaseId,
  catalogueId,
  visitorId,
  openId,
  productId,
  orderId,
  inquiryId
}) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/firebase-analytics/product-selected-event',
    params: {
      eventId,
      showcaseId,
      catalogueId,
      visitorId,
      openId,
      productId,
      orderId,
      inquiryId
    }
  });

export const firebaseAnalyticsProductUnselectedEvent = ({
  eventId,
  showcaseId,
  catalogueId,
  visitorId,
  openId,
  productId,
  orderId,
  inquiryId
}) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/firebase-analytics/product-unselected-event',
    params: {
      eventId,
      showcaseId,
      catalogueId,
      visitorId,
      openId,
      productId,
      orderId,
      inquiryId
    }
  });

export const fetchIncentivizedCouponCode = ({ companyId }) =>
  httpRequestLib({
    method: 'GET',
    url: `/api/incentive/coupon?companyId=${companyId}`
  });

export const availIncentivizedCoupon = (params) =>
  httpRequestLib({
    method: 'POST',
    url: '/api/incentive/coupon',
    params
  });

export const fetchCustomerOrders = ({ companyId = getCompanyId(), pageData } = {}) =>
  httpRequestLib({
    method: 'GET',
    url: `/api/order/company/${companyId}/customer-orders${createQueryParams(
      pageData ? [{ key: 'pageData', value: JSON.stringify(pageData) }] : null
    )}`
  });

export const fetchCustomerOrderDetails = ({ orderId } = {}) => {
  const userId = getUserId();
  return httpRequestLib({
    method: 'GET',
    url: `/api/order/company-user/${userId}/customer-orders/${orderId}`
  });
};

export const downloadCustomerOrderPdf = ({ orderId, visitorId }) => {
  const userId = getUserId();
  return httpRequestLib({
    method: 'POST',
    url: '/api/order/company-user/customer/pdf/download',
    params: { orderId, visitorId, userId }
  });
};

export const deleteVisitorAccount = ({ companyId = getCompanyId() } = {}) =>
  httpRequestLib({
    method: 'DELETE',
    url: '/api/visitor/account-delete',
    params: {
      visitorId: _visitor.id,
      companyId
    }
  });
